import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui';
import { useState } from 'react';

export default function RouteErrorPage() {
  const error = useRouteError();
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred while loading this page.';
  let statusCode: number | null = null;
  let errorMessage = 'Unknown error';
  let errorStack = '';

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    switch (error.status) {
      case 404:
        title = 'Page Not Found';
        message = "Sorry, we couldn't find the page you're looking for.";
        break;
      case 403:
        title = 'Access Denied';
        message = "You don't have permission to access this page.";
        break;
      case 401:
        title = 'Authentication Required';
        message = 'Please log in to access this page.';
        break;
      case 500:
        title = 'Server Error';
        message = 'Our servers encountered an error. Please try again later.';
        break;
      default:
        title = `Error ${error.status}`;
        message = error.statusText || 'An error occurred.';
    }
    errorMessage = error.statusText;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorStack = error.stack || '';
  }

  const handleRefresh = () => {
    window.location.reload();
  };

  const copyErrorDetails = async () => {
    const details = `Error: ${errorMessage}
${errorStack ? `Stack: ${errorStack}` : ''}
URL: ${window.location.href}
Timestamp: ${new Date().toISOString()}`;

    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('Failed to copy');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-red-50 border-b border-red-100 p-6 text-center">
            {statusCode && (
              <p className="text-6xl font-bold text-gray-200 mb-2">{statusCode}</p>
            )}
            {!statusCode && (
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            )}
            <h1 className="text-xl font-semibold text-gray-900 mb-2">{title}</h1>
            <p className="text-gray-600 text-sm">{message}</p>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="primary"
                leftIcon={<RefreshCw className="w-4 h-4" />}
                onClick={handleRefresh}
              >
                Refresh Page
              </Button>
              <Link to="/">
                <Button variant="secondary" leftIcon={<Home className="w-4 h-4" />}>
                  Go Home
                </Button>
              </Link>
            </div>

            {!isRouteErrorResponse(error) && (
              <div className="border-t border-gray-100 pt-4">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center justify-between w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <span>Technical Details</span>
                  {showDetails ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>

                {showDetails && (
                  <div className="mt-3 space-y-3">
                    <div className="bg-gray-50 rounded-lg p-3 overflow-auto">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">
                          Error Message
                        </span>
                        <button
                          onClick={copyErrorDetails}
                          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                        >
                          {copied ? (
                            <>
                              <Check className="w-3 h-3" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-sm text-red-600 font-mono break-all">{errorMessage}</p>
                    </div>

                    {errorStack && (
                      <div className="bg-gray-50 rounded-lg p-3 overflow-auto max-h-48">
                        <span className="text-xs font-medium text-gray-500 uppercase block mb-2">
                          Stack Trace
                        </span>
                        <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap break-all">
                          {errorStack}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          If this problem persists, please contact{' '}
          <a
            href="mailto:support@greengeeks.com"
            className="text-green-600 hover:text-green-700"
          >
            support@greengeeks.com
          </a>
        </p>
      </div>
    </div>
  );
}
